import type { VisitClinicRezept } from '@/types/visitClinic.types';

/**
 * HIT-15106 (CR No.8, BD VN 1.2.2 §8.18 / 1.3.2 §2.1) — 「当月施術内容が同意書と異なる理由」.
 * One box per 料金 table (本レセ). The 詳細編集 modal reads the box of the table that owns the
 * edited day to decide whether to show its reminder, and its OK button scrolls to that box.
 */
export const CONSENT_DIFFERENCE_REASON_MAX_LENGTH = 255;

export const PRICE_SECTION_ELEMENT_ID = 'visit-clinic-price-section';
export const rezeptPriceBlockElementId = (rezeptId: number): string =>
  `visit-clinic-rezept-${rezeptId}`;
export const consentDifferenceReasonElementId = (rezeptId: number): string =>
  `consent-difference-reason-${rezeptId}`;

/**
 * The 料金 table a day belongs to (BD 1.3.2 §2.1, 2026/10/30):
 *  - the day is registered → the table whose rezept is the day's rezept_id;
 *  - not registered yet → the table whose 保険証 / 受診者情報 are valid that day;
 *    none can be told → the first table;
 *  - the month has no rezept → null (no table, nothing to focus).
 */
export function resolveRezeptForDay(
  date: string,
  visitRezeptId: number | null | undefined,
  rezepts: VisitClinicRezept[],
): VisitClinicRezept | null {
  if (rezepts.length === 0) return null;
  if (visitRezeptId) {
    const owner = rezepts.find(r => r.id === Number(visitRezeptId));
    if (owner) return owner;
  }
  const covering = rezepts.find(
    r =>
      (!!r.coverage_from || !!r.coverage_to) &&
      (!r.coverage_from || r.coverage_from <= date) &&
      (!r.coverage_to || date <= r.coverage_to),
  );
  return covering ?? rezepts[0];
}

/** String.prototype.trim also strips the full-width space (U+3000). */
export function hasConsentDifferenceReason(rezept: VisitClinicRezept | null): boolean {
  return (rezept?.consent_difference_reason ?? '').trim() !== '';
}

/**
 * Wait for the 詳細編集 modal and the reminder to finish closing: antd hands focus back to
 * the element that opened them when their close animation ends, which would otherwise take
 * the focus straight back off the reason box.
 */
const REVEAL_DELAY_MS = 400;

/**
 * OK on the reminder: published rezept → scroll to its table only (the box is disabled);
 * no rezept this month → scroll to 「料金｜コメント」 only; otherwise scroll to the box and focus it.
 */
export function revealConsentDifferenceReason(rezept: VisitClinicRezept | null): void {
  window.setTimeout(() => {
    const scroll = (el: HTMLElement | null, block: ScrollLogicalPosition) =>
      el?.scrollIntoView({ behavior: 'smooth', block });

    if (!rezept) {
      scroll(document.getElementById(PRICE_SECTION_ELEMENT_ID), 'start');
      return;
    }
    if (rezept.all_status !== 0) {
      scroll(document.getElementById(rezeptPriceBlockElementId(rezept.id)), 'center');
      return;
    }
    const field = document.getElementById(consentDifferenceReasonElementId(rezept.id));
    scroll(field, 'center');
    field?.focus({ preventScroll: true });
  }, REVEAL_DELAY_MS);
}
